import { useEffect, useState } from 'react';

/** Live preview of a plan card, drawn to match the user-app "My Plan" page. */

export interface FeatureItem {
  name: string;
  info?: string;
  badge?: string;
  comingSoon?: boolean;
}

export const DEFAULT_ACCENT = '#0068a3';

export interface PreviewProps {
  name: string;
  description: string;
  priceCents: number;
  perSite: boolean;
  /** Slider range for a per-site plan; blank falls back to 1–100. */
  minSites?: number | null;
  maxSites?: number | null;
  /** Where the customer's slider opens; blank falls back to 10 (clamped into the range). */
  defaultSites?: number | null;
  /**
   * The customer sees the price Stripe bills for this plan, not the price typed here
   * (a plan that starts checkout without "Charge this price"). The typed price is dimmed,
   * the saving line is hidden, and a note says so — the preview must not promise a number
   * the customer will not see.
   */
  stripePriced?: boolean;
  /** Volume discounts: the per-site price from that many sites up. The whole count is billed at its band's rate. */
  tiers?: { from: number; priceCents: number }[];
  /** Checkout bills the configured price (Agency in flat or tiers mode); drives the explanatory note. */
  chargesThisPrice?: boolean;
  interval: 'month' | 'year' | 'one_time';
  credits: number;
  accent: string;
  ctaLabel: string;
  features: FeatureItem[];
  enabled: boolean;
}

const usd = (cents: number) => {
  const v = cents / 100;
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
};
const PER = { month: '/mo', year: '/yr', one_time: '' } as const;

/**
 * What customers pay per site while no price is saved here: the original Stripe volume price (user-app's
 * AGENCY_VOLUME_BANDS), which My Plan falls back to. Only used to fill {perSite} in the preview.
 */
const BUILT_IN_BANDS = [{ upTo: 9, cents: 2500 }, { upTo: 49, cents: 2200 }, { upTo: 99, cents: 2000 }, { upTo: Infinity, cents: 1950 }];

/** The same placeholders and wording My Plan fills in (user-app planCardsShared), so the preview reads as customers see it. */
function fillFeatureName(name: string, sites: number, perSite: string): string {
  const filled = name.replace(/\{sites\}/g, String(sites)).replace(/\{perSite\}/g, perSite);
  return sites === 1 ? filled.replace('site licenses', 'site license') : filled;
}

/** Width of the slider thumb, as in My Plan. */
const THUMB_PX = 24;

/** The marks My Plan draws: the minimum, quarters of the maximum, and the maximum. */
function ticksFor(min: number, max: number): number[] {
  return [min, ...[0.25, 0.5, 0.75].map((f) => Math.round(max * f)), max]
    .filter((n, i) => i === 0 || n > min)
    .filter((n, i, a) => n <= max && a.indexOf(n) === i);
}

export default function PlanCardPreview(p: PreviewProps) {
  const min = Math.max(1, p.minSites ?? 1);
  const max = Math.max(min, p.maxSites ?? 100);
  const opening = p.defaultSites ?? 10;
  const [picked, setPicked] = useState(opening);
  // The slider opens where customers' does; editing the opening count moves it.
  useEffect(() => setPicked(opening), [opening]);
  const [open, setOpen] = useState(false);
  const sites = Math.min(max, Math.max(min, picked));
  const pct = max === min ? 0 : ((sites - min) / (max - min)) * 100;
  const accent = /^#[0-9a-fA-F]{6}$/.test(p.accent) ? p.accent : DEFAULT_ACCENT;
  const header = /plan$/i.test(p.name.trim()) ? p.name.trim() : `${p.name.trim() || 'New'} Plan`;
  // The rate of the band the whole count falls in (Stripe's volume mode), against the base price.
  const band = (p.tiers ?? []).filter((t) => t.from <= sites).sort((a, b) => b.from - a.from)[0];
  // With no price saved here customers pay the original Stripe volume rates, so show those rather than a blank.
  const builtIn = p.stripePriced && p.perSite;
  const perSiteCents = builtIn ? (BUILT_IN_BANDS.find((b) => sites <= b.upTo)?.cents ?? p.priceCents) : band ? band.priceCents : p.priceCents;
  const savedPct = p.priceCents > 0 && perSiteCents < p.priceCents ? Math.round((1 - perSiteCents / p.priceCents) * 100) : 0;
  return (
    <div className={'plan-preview' + (p.enabled ? '' : ' plan-preview--off')} style={{ ['--plan-accent' as string]: accent }} data-testid="plan-preview">
      <div className="plan-preview-header">{header}</div>
      <div className="plan-preview-body">
        <div className={'plan-preview-price' + (p.stripePriced && !p.perSite ? ' plan-preview-price--dim' : '')}>
          {p.stripePriced && !p.perSite
            ? "Stripe's price"
            : <>{p.perSite ? usd(perSiteCents * sites) : p.priceCents === 0 ? '$0' : usd(p.priceCents)}{PER[p.interval]}</>}
        </div>
        {p.stripePriced && (
          <div className="plan-preview-note plan-preview-note--warn" data-testid="stripe-priced-note">
            Customers see the price Stripe bills for this plan (set in Stripe), not this price.
          </div>
        )}
        {p.description && <div className="plan-preview-sub">{p.description}</div>}
        {p.perSite ? (
          <>
            <div className="plan-preview-pill">{sites} site license{sites === 1 ? '' : 's'}</div>
            <div className="plan-preview-slider">
              <div className="plan-preview-slider-label">
                <span>Site licenses</span>
                <strong>{p.stripePriced && !p.perSite ? "Stripe's price" : `${usd(perSiteCents)}/site`}</strong>
              </div>
              {/* Drawn as My Plan draws it: the rail holds a track and its fill, the input rides on top, and the
                  ticks sit under it, each corrected for the thumb's width so they meet it. */}
              <div className="plan-preview-rail">
                <div className="plan-preview-track">
                  <div className="plan-preview-track-fill" style={{ width: `calc(${pct}% + ${(0.5 - pct / 100) * THUMB_PX}px)` }} />
                </div>
                <input
                  type="range"
                  aria-label="Preview site licenses"
                  min={min}
                  max={max}
                  step={1}
                  value={sites}
                  onChange={(e) => setPicked(Number(e.target.value))}
                />
              </div>
              <div className="plan-preview-ticks">
                {ticksFor(min, max).map((n) => {
                  const at = max > min ? (n - min) / (max - min) : 0;
                  return (
                    <button
                      key={n}
                      type="button"
                      className={'plan-preview-tick' + (sites === n ? ' plan-preview-tick--on' : '')}
                      style={{ left: `calc(${at * 100}% + ${(0.5 - at) * THUMB_PX}px)` }}
                      onClick={() => setPicked(n)}
                    >
                      <span className="plan-preview-tick-mark" aria-hidden="true" />
                      {n}
                    </button>
                  );
                })}
              </div>
              {savedPct > 0 && !p.stripePriced && (
                <div className="plan-preview-note" data-testid="preview-saving">
                  Volume price: save {savedPct}% (instead of {usd(p.priceCents * sites)})
                </div>
              )}
              <div className="plan-preview-note">
                Plus the site included with your account. Need more than {max}? <u>Talk to us</u>
              </div>
              <div className="plan-preview-note">
                {p.chargesThisPrice
                  ? 'Total is price × sites, billed by checkout.'
                  : p.stripePriced
                    ? 'The total is calculated by checkout from the Stripe price.'
                    : 'Total is price × licences.'}
              </div>
            </div>
          </>
        ) : (
          <div className="plan-preview-pill">{p.credits.toLocaleString()} credits</div>
        )}
        <button
          type="button"
          className="plan-preview-label plan-preview-label--button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span>Key Features</span>
          <svg className={'plan-preview-chevron' + (open ? ' plan-preview-chevron--open' : '')} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <ul className={'plan-preview-features' + (open ? '' : ' plan-preview-features--closed')}>
          {p.features.filter((f) => f.name.trim()).map((f, i) => (
            <li key={i}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="8.3" fill={accent} fillOpacity="0.15" stroke={accent} strokeWidth="1.5" />
                <path d="M7 10l2 2 4-4.2" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{p.stripePriced && !p.perSite ? f.name : fillFeatureName(f.name, sites, usd(perSiteCents))}</span>
              {f.info && <span className="plan-preview-info" title={f.info}>ⓘ</span>}
              {f.badge && <span className="plan-preview-badge">{f.badge}</span>}
              {f.comingSoon && <span className="plan-preview-soon">coming soon</span>}
            </li>
          ))}
        </ul>
        <div className="plan-preview-cta">{p.ctaLabel.trim() || 'Upgrade'}</div>
      </div>
    </div>
  );
}
